// src/services/standardUserApi.js
// For standard user data stored in Postgres.

/**
 * Reusable function for doing a fetch and parsing the response as JSON,
 * throwing an error if the response is not OK.
 */
async function fetchJSON(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error("Error in standardUserApi:", errorData);
    throw new Error(errorData.error || `Fetch error: ${response.status}`);
  }
  return response.json();
}

/**
 * Helper that sets up standard request options like credentials and headers,
 * then calls fetchJSON.
 */
async function doFetch(token, path, method = "GET", body = null) {
  const options = {
    method,
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  if (body) {
    options.headers["Content-Type"] = "application/json";
    options.body = JSON.stringify(body);
  }

  return fetchJSON(path, options);
}


export const standardUserApi = {
  /**
   * firstSignup() - special route for brand-new signups
   * This route does NOT use @standard_user_required on the server side,
   * so it can upsert the user in Postgres if not found.
   */
  firstSignup: async (token, profileData) => {
    return doFetch(token, "/api/signup/firstSignup", "POST", profileData);
  },

  getUserProfile: async (token) => {
    return doFetch(token, "/api/standard-user/getProfile");
  },

  // Add this new method to standardUserApi object:
  getFutureSlotsPublic: async () => {
    return fetchJSON("/api/open/future-slots");
  },

  updateUserProfile: async (token, updatedProfile) => {
    return doFetch(token, "/api/standard-user/updateProfile", "PUT", updatedProfile);
  },

  getUserGatherings: async (token) => {
    return doFetch(token, "/api/standard-user/gatherings");
  },

  dropOutOfGathering: async (token, gatheringId) => {
    return doFetch(token, "/api/standard-user/dropOut", "POST", { gatheringId });
  },

  removeUserSlot: async (token, slotId) => {
    return doFetch(token, "/api/standard-user/remove-user-slot", "POST", { slotId });
  },

  // Retrieve lookups (genders, holidays, topics,
  // movie genres, music genres, ideal weekends, seeking traits, industries and jobs)
  getLookupData: async (token) => {
    return doFetch(token, "/api/open/lookups");
  },

  getLocationsForCascadingDropdowns: async (token) => {
    return doFetch(token, "/api/open/locations-rows");
  },

  getActiveThemeIds: async (locationId) => {
    return fetchJSON(`/api/open/active-theme-ids?location_id=${locationId}`);
  },

  getFutureSlots: async (token, locationId) => {
    const url = locationId
      ? `/api/open/future-slots?location_id=${locationId}`
      : "/api/open/future-slots";
    return doFetch(token, url);
  },

  getUserLocation: async (token) => {
    return doFetch(token, "api/standard-user/get-user-location", "GET")
  },

  updateUserSlots: async (token, payload) => {
    return doFetch(token, "/api/standard-user/update-user-slots", "POST", payload);
  },

  sendSlotConfirmation: async (token, userId) => {
    return doFetch(token, "/api/standard-user/send-slot-confirmations", userId);
  },

  getUserSlots: async (token) => {
    return doFetch(token, "/api/standard-user/get-user-slots");
  },

  getMeetupEventRsvpStatus: async (token, eventIds) => {
    return doFetch(token, `/api/standard-user/meetup-event-rsvp-status?event_ids=${eventIds.map(encodeURIComponent).join(",")}`);
  },

  rsvpMeetupEvent: async (token, meetupEventId) => {
    return doFetch(token, "/api/standard-user/rsvp-meetup-event", "POST", { meetup_event_id: meetupEventId });
  },

  dropOutOfMeetupEvent: async (token, meetupEventId) => {
    return doFetch(token, "/api/standard-user/rsvp-meetup-event", "DELETE", { meetup_event_id: meetupEventId });
  },

  getMyEvents: async (token) => {
    return doFetch(token, "/api/standard-user/my-events");
  },

  recordBlogView: async (token, blogSlug) => {
    return doFetch(token, "/api/standard-user/record-blog-view", "POST", { blog_slug: blogSlug });
  },

  createInviteLink: async (token, { themeId, gatheringId }) => {
    return doFetch(token, "/api/invite-links", "POST", { themeId, gatheringId });
  },

  attachInviteToken: async (token, inviteToken) => {
    return doFetch(token, "/api/user/attach-invite-token", "POST", { token: inviteToken });
  },

  // Public: a visitor arriving via an invite link isn't necessarily logged
  // in yet, so this doesn't go through doFetch's Authorization header.
  trackInviteLinkClick: async (inviteToken) => {
    return fetchJSON("/api/invite-links/track-click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: inviteToken }),
    });
  },

  // ---- Connections / Circles / Self-Run Events (all subscriber-only) ----

  getConnections: async (token) => {
    return doFetch(token, "/api/connections");
  },

  createConnection: async (token, { connectedUserId, gatheringId }) => {
    return doFetch(token, "/api/connections", "POST", { connectedUserId, gatheringId });
  },

  acceptConnection: async (token, connectionId) => {
    return doFetch(token, `/api/connections/${connectionId}/accept`, "POST");
  },

  getMyConnectionLink: async (token) => {
    return doFetch(token, "/api/connections/my-link");
  },

  getConnectionLinkPreview: async (shareToken) => {
    return fetchJSON(`/api/connections/via-link/${shareToken}`);
  },

  acceptConnectionViaLink: async (token, shareToken) => {
    return doFetch(token, `/api/connections/via-link/${shareToken}/accept`, "POST");
  },

  getCircles: async (token) => {
    return doFetch(token, "/api/circles");
  },

  createCircle: async (token, { name, memberUserIds }) => {
    return doFetch(token, "/api/circles", "POST", { name, memberUserIds });
  },

  addCircleMember: async (token, circleId, userId) => {
    return doFetch(token, `/api/circles/${circleId}/members`, "POST", { userId });
  },

  getPendingCircleInvites: async (token) => {
    return doFetch(token, "/api/circles/pending-invites");
  },

  acceptCircleInvite: async (token, circleId) => {
    return doFetch(token, `/api/circles/${circleId}/accept`, "POST");
  },

  leaveCircle: async (token, circleId) => {
    return doFetch(token, `/api/circles/${circleId}/leave`, "POST");
  },

  getSelfRunEvents: async (token) => {
    return doFetch(token, "/api/self-run-events");
  },

  createSelfRunEvent: async (token, { title, description, eventDatetime, location, visibility, circleId }) => {
    return doFetch(token, "/api/self-run-events", "POST", {
      title, description, eventDatetime, location, visibility, circleId,
    });
  },

  rsvpSelfRunEvent: async (token, eventId) => {
    return doFetch(token, `/api/self-run-events/${eventId}/rsvp`, "POST");
  },

  // Public: the /join/:token guest page has no account, so these never
  // send an Authorization header (unless the visitor happens to already
  // be logged in - see JoinEvent.js, which passes the token itself when
  // available so the backend can RSVP them as a member instead of a guest).
  getJoinEvent: async (joinToken) => {
    return fetchJSON(`/api/join/${joinToken}`);
  },

  rsvpToJoinEvent: async (joinToken, { guestName, guestEmail }, memberToken = null) => {
    const headers = { "Content-Type": "application/json" };
    if (memberToken) {
      headers.Authorization = `Bearer ${memberToken}`;
    }
    return fetchJSON(`/api/join/${joinToken}/rsvp`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ guestName, guestEmail }),
    });
  },

  // ---- Cadence Reminders ("Stay In Touch") - subscriber-only ----

  getCadenceReminders: async (token) => {
    return doFetch(token, "/api/cadence-reminders");
  },

  setCadenceReminder: async (token, { connectionId, circleId, cadenceDays }) => {
    return doFetch(token, "/api/cadence-reminders", "POST", { connectionId, circleId, cadenceDays });
  },

  markCadenceReminderCaughtUp: async (token, reminderId) => {
    return doFetch(token, `/api/cadence-reminders/${reminderId}/mark-caught-up`, "POST");
  },

  deleteCadenceReminder: async (token, reminderId) => {
    return doFetch(token, `/api/cadence-reminders/${reminderId}`, "DELETE");
  },

  // ---- Wishlist ----

  getWishlist: async (token) => {
    return doFetch(token, "/api/wishlist");
  },

  createWishlistItem: async (token, { title, description, isPublic }) => {
    return doFetch(token, "/api/wishlist", "POST", { title, description, isPublic });
  },

  updateWishlistItem: async (token, itemId, updates) => {
    return doFetch(token, `/api/wishlist/${itemId}`, "PUT", updates);
  },

  deleteWishlistItem: async (token, itemId) => {
    return doFetch(token, `/api/wishlist/${itemId}`, "DELETE");
  },

  shareWishlistItem: async (token, itemId, { connectedUserId, circleId }) => {
    return doFetch(token, `/api/wishlist/${itemId}/share`, "POST", { connectedUserId, circleId });
  },

  getWishlistShareToken: async (token) => {
    return doFetch(token, "/api/wishlist/share-token");
  },

  // Public: the /wishlist/:token guest page has no account.
  getPublicWishlist: async (shareToken) => {
    return fetchJSON(`/api/wishlist/public/${shareToken}`);
  },

  registerWishlistInterest: async (shareToken, itemId, { guestName, guestEmail }, memberToken = null) => {
    const headers = { "Content-Type": "application/json" };
    if (memberToken) {
      headers.Authorization = `Bearer ${memberToken}`;
    }
    return fetchJSON(`/api/wishlist/public/${shareToken}/items/${itemId}/interest`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ guestName, guestEmail }),
    });
  },

};
